import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import NavBar from './NavBar';
import wiproLogo from './assets/wipro.png';
import './TicketDetailsPage.css';
import { FaArrowLeft, FaFileAlt, FaCheck, FaSave, FaSpinner, FaThumbsUp, FaThumbsDown, FaCopy, FaSearch, FaPaperPlane, FaTimes, FaTicketAlt, FaExternalLinkAlt, FaRegLightbulb, FaSyncAlt, FaComment } from 'react-icons/fa';
import { searchResolutionSteps } from './services/ragService';

const EXCEPTION_EMAILS = new Set([
  'admin@example.com',
  'deepak.mittal@contractor.tranetechnologies.com',
  'mukesh.panwar@contractor.tranetechnologies.com',
  'vipul.narayan.singh@contractor.tranetechnologies.com',
  'harishchandra.karnatak@contractor.tranetechnologies.com',
  'brian.beirne@contractor.tranetechnologies.com',
  'craig.friell@contractor.tranetechnologies.com',
  'mark.hainsworth@contractor.tranetechnologies.com',
  'mukesh.kumar@contractor.tranetechnologies.com',
  'nikita.kapadia@contractor.tranetechnologies.com',
  'rajesh.araveeti@contractor.tranetechnologies.com',
  'ravikumar.n@contractor.tranetechnologies.com',
  'rewanth.reddy@contractor.tranetechnologies.com',
  'shabeer.shaik@contractor.tranetechnologies.com',
  'sri.chitti.durga.rao@contractor.tranetechnologies.com',
  'swapna.mekala@contractor.tranetechnologies.com',
  'vikram.kumar@contractor.tranetechnologies.com'
]);

const normalizeEmail = (value) => String(value || '').trim().toLowerCase();

const normalizePriorityValue = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return '';
  const match = raw.match(/^([1-4])/);
  return match ? match[1] : '';
};

// ServiceNow priority = f(impact, urgency)
// https://docs.servicenow.com/bundle/washingtondc-it-service-management/page/product/incident-management/concept/incident-priority.html
const calculatePriorityFromImpactUrgency = (impact, urgency) => {
  const matrix = {
    '1': { '1': '1', '2': '2', '3': '3' },
    '2': { '1': '2', '2': '3', '3': '4' },
    '3': { '1': '3', '2': '4', '3': '4' },
  };
  return (matrix[impact] || {})[urgency] || '';
};

const PRIORITY_LABELS = { '1': 'Critical', '2': 'High', '3': 'Moderate', '4': 'Low' };

const generateSampleData = () => {
  const statuses = ['Open', 'In Progress', 'Resolved'];
  const assignmentGroups = ['Network Team', 'App Support', 'Security'];
  const users = ['Alice', 'Bob', 'Charlie'];
  const descriptions = ['Email issue', 'VPN not working', 'Access denied', 'System crash', 'Password reset'];
  const items = ['Laptop', 'VPN', 'Email', 'Software', 'Access'];
  const priorities = ['High', 'Medium', 'Low'];

  return Array.from({ length: 20 }, (_, i) => {
    const index = i + 1;
    return {
      ticketId: `INC${index.toString().padStart(4, '0')}`,
      shortDescription: descriptions[i % descriptions.length],
      assignmentGroup: assignmentGroups[i % assignmentGroups.length],
      receivedOn: `2025-06-${String(i % 30 + 1).padStart(2, '0')}`,
      assignedTo: users[i % users.length],
      status: statuses[i % statuses.length],
      item: items[i % items.length],
      priority: priorities[i % priorities.length],
      action: 'Review'
    };
  });
};

// User-based caching utilities
const RAG_CACHE_PREFIX = 'rag_cache_';
const CACHE_EXPIRY_TIME = 10 * 60 * 1000; // 10 minutes in milliseconds

const getUserTicketCacheKey = (userEmail, ticketId) => {
  const userId = userEmail ? userEmail.toLowerCase() : 'anonymous';
  return `${RAG_CACHE_PREFIX}${userId}_${ticketId}`;
};

const getCachedResolutions = (userEmail, ticketId) => {
  try {
    const cacheKey = getUserTicketCacheKey(userEmail, ticketId);
    const cached = localStorage.getItem(cacheKey);
    
    if (!cached) return null;
    
    const parsedCache = JSON.parse(cached);
    const now = Date.now();
    
    // Check if cache has expired
    if (now - parsedCache.timestamp > CACHE_EXPIRY_TIME) {
      localStorage.removeItem(cacheKey);
      return null;
    }
    
    return parsedCache.data;
  } catch (error) {
    console.error('Error reading cache:', error);
    return null;
  }
};

const setCachedResolutions = (userEmail, ticketId, data) => {
  try {
    const cacheKey = getUserTicketCacheKey(userEmail, ticketId);
    const cacheData = {
      timestamp: Date.now(),
      data: data
    };
    localStorage.setItem(cacheKey, JSON.stringify(cacheData));
  } catch (error) {
    console.error('Error writing cache:', error);
  }
};

const clearUserTicketCache = (userEmail, ticketId) => {
  try {
    const cacheKey = getUserTicketCacheKey(userEmail, ticketId);
    localStorage.removeItem(cacheKey);
  } catch (error) {
    console.error('Error clearing cache:', error);
  }
};

const formatResolutionStepsForDisplay = (steps = []) => {
  let inSourcesSection = false;
  let sourceIndex = 1;

  return steps.map((step) => {
    const raw = String(step || '').trim();
    if (!raw) return '';

    const cleanStep = raw
      .replace(/^[-*\u2022]\s*/, '')
      .replace(/^\d+[.)]\s*/, '')
      .trim();

    if (!cleanStep) return '';

    const compactText = cleanStep.replace(/[\u2500-\u257f\-\s]/g, '');
    if (/^sources?\b/i.test(cleanStep) || /\bsources?\b/i.test(compactText)) {
      inSourcesSection = true;
      return '___SOURCES___';
    }

    if (inSourcesSection) {
      const numbered = `${sourceIndex}. ${cleanStep}`;
      sourceIndex += 1;
      return numbered;
    }

    if (/\bquick\s*checks?\b/i.test(cleanStep) || /\bescalation\s*steps?\b/i.test(cleanStep)) {
      return `___SECTION___${cleanStep.replace(/^[\u2500\-\s]+|[\u2500\-\s]+$/g, '').trim()}`;
    }

    return `- ${cleanStep}`;
  }).filter(Boolean).join('\n');
};

const RecommendationsRenderer = ({ text }) => {
  if (!text) return null;
  const lines = text.split('\n').filter(l => l.trim());
  let sourceIdx = 1;
  return (
    <div className="rec-renderer">
      {lines.map((line, i) => {
        const t = line.trim();
        if (t.startsWith('___SECTION___')) {
          const label = t.replace('___SECTION___', '');
          return (
            <div key={i} className="rec-section-header">
              <span className="rec-section-dash">──</span>
              <span className="rec-section-label">{label}</span>
              <span className="rec-section-dash rec-section-dash-end">──────────────</span>
            </div>
          );
        }
        if (t === '___SOURCES___') {
          sourceIdx = 1;
          return <div key={i} className="rec-sources-header">── Sources ────────────────</div>;
        }
        if (t.startsWith('- ')) {
          return (
            <div key={i} className="rec-step">
              <span className="rec-bullet">•</span>
              <span className="rec-step-text">{t.slice(2)}</span>
            </div>
          );
        }
        const srcMatch = t.match(/^(\d+)\. (.+)/);
        if (srcMatch) {
          const n = srcMatch[1];
          const val = srcMatch[2];
          return (
            <div key={i} className="rec-source-item">
              <span className="rec-source-num">{n}.</span>
              <span className="rec-source-val">{val}</span>
            </div>
          );
        }
        return <div key={i} className="rec-plain-line">{t}</div>;
      })}
    </div>
  );
};

const TicketDetailsPage = ({ userName, userEmail, onLogout, canViewUserAnalytics = false }) => {
  const { ticketId } = useParams();
  const isSctask = /^SCTASK\d+$/i.test(ticketId || '');
  const [ticket, setTicket] = useState({})
  const [ticketLoadError, setTicketLoadError] = useState('');

  // RAG API states
  const [ragResolutionSteps, setRagResolutionSteps] = useState([]);
  const [relatedResources, setRelatedResources] = useState([]);
  const [ragLoading, setRagLoading] = useState(false);
  const [ragError, setRagError] = useState(null);
  const [similarTickets, setSimilarTickets] = useState([]);
  const [selectedSteps, setSelectedSteps] = useState(new Set());
  const [activeTab, setActiveTab] = useState('similar');
  const [isFromCache, setIsFromCache] = useState(false);
  const [streamStatus, setStreamStatus] = useState('');
  const [isStreamingPreview, setIsStreamingPreview] = useState(false);

  // Feedback states
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackText, setFeedbackText] = useState('');
  const [feedbackReason, setFeedbackReason] = useState('');
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);

  // User Query states
  const [userQuery, setUserQuery] = useState('');
  const [showUserQueryInput, setShowUserQueryInput] = useState(false);
  const [userQueryLoading, setUserQueryLoading] = useState(false);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [groupUsers, setGroupUsers] = useState([]);
  const [groupUsersLoading, setGroupUsersLoading] = useState(false);
  const [selectedAssignedTo, setSelectedAssignedTo] = useState('');
  const [selectedPriority, setSelectedPriority] = useState('');
  const [selectedImpact, setSelectedImpact] = useState('');
  const [selectedUrgency, setSelectedUrgency] = useState('');
  const [attachments, setAttachments] = useState([]);

  const applyTicketDetailsToState = (data) => {
    setTicket(data || {});
    setSelectedAssignedTo(data?.assignedTo || '');
    setSelectedPriority(normalizePriorityValue(data?.priority));
    setSelectedImpact(normalizePriorityValue(data?.impact || ''));
    setSelectedUrgency(normalizePriorityValue(data?.urgency || ''));
    setConfigurationItem(data?.item || '');

    const incomingStatus = String(data?.status || '').trim();
    if (incomingStatus === 'On Hold') {
      setState('On Hold');
    } else if (incomingStatus === 'Resolved') {
      setState('Resolved');
    } else if (incomingStatus === 'Canceled' || incomingStatus === 'Cancelled') {
      setState('Canceled');
    } else {
      setState('In Progress');
    }

    const holdReasonRaw = String(data?.holdReason || '').trim();
    setOnHoldReason(holdReasonCodeToLabelMap[holdReasonRaw] || holdReasonRaw || '');
  };

  useEffect(() => {
    const API_URL = `/api/tickets/${ticketId}`
    
    console.log('Fetching ticket:', ticketId);
    console.log('API URL:', API_URL);

    setTicketLoadError('');

    fetch(API_URL, { method: "GET" })
      .then(async (res) => {
        console.log('Response status:', res.status);
        const data = await res.json();
        if (!res.ok) {
          throw new Error(data?.error || 'Failed to load ticket details');
        }
        console.log('Ticket data received:', data);
        applyTicketDetailsToState(data);
      }).catch((error) => {
        console.error('Error fetching ticket:', error);
        setTicketLoadError(error.message || 'Failed to load ticket details');
      })
  }, [ticketId])

  useEffect(() => {
    if (!ticket?.assignmentGroup && !ticket?.assignmentGroupSysId) {
      setGroupUsers([]);
      return;
    }

    const fetchGroupUsers = async () => {
      setGroupUsersLoading(true);
      try {
        const params = new URLSearchParams();
        if (ticket.assignmentGroup) {
          params.set('assignmentGroup', ticket.assignmentGroup);
        }
        if (ticket.assignmentGroupSysId) {
          params.set('assignmentGroupSysId', ticket.assignmentGroupSysId);
        }

        const response = await fetch(`/api/assignment-group-users?${params.toString()}`);
        const result = await response.json();

        if (!response.ok || !result.success) {
          throw new Error(result?.error || 'Failed to load assignment group users');
        }

        setGroupUsers(result.users || []);
      } catch (error) {
        console.error('Error fetching assignment group users:', error);
        setGroupUsers([]);
      } finally {
        setGroupUsersLoading(false);
      }
    };

    fetchGroupUsers();
  }, [ticket?.assignmentGroup, ticket?.assignmentGroupSysId]);

  // Fetch RAG resolution steps when ticket is loaded
  useEffect(() => {
    if (ticket && ticket.shortDescription) {
      // Combine short description and description for better context
      const combinedQuery = ticket.description 
        ? `${ticket.shortDescription}\n\n${ticket.description}`
        : ticket.shortDescription;
      
      fetchRAGResolutions(combinedQuery, {
        assignment_group: ticket.assignmentGroup || '',
        cmdb_ci: ticket.item || '',
        routing_app: ticket.routingServiceApp || '',
      });
    }
  }, [ticket]);

  const fetchRAGResolutions = async (query, context = {}) => {
    setRagError(null);
    
    // Check if we have cached data for this user + ticket
    const cachedData = getCachedResolutions(userEmail, ticketId);
    
    if (cachedData) {
      // Load from cache without showing loading spinner
      console.log('Loading from cache for user:', userEmail, 'ticket:', ticketId);
      setIsFromCache(true);
      setRagResolutionSteps(cachedData.steps || []);
      setRecommendations(
        formatResolutionStepsForDisplay(cachedData.steps || []) ||
        cachedData.formattedRecommendations ||
        ''
      );
      setResolutionNotes('');
      setRelatedResources(cachedData.uniqueResources || []);
      setSimilarTickets(cachedData.similar_items || []);
      return;
    }
    
    // No cache, fetch from API
    setRagLoading(true);
    setIsFromCache(false);
    setIsStreamingPreview(false);
    setStreamStatus('Connecting to RAG stream...');
    const startTime = Date.now();
    
    try {
      const response = await searchResolutionSteps(query, context, {
        onStatus: (message) => {
          setStreamStatus(message || 'Generating AI recommendations...');
        },
        onToken: (fullText) => {
          setIsStreamingPreview(true);
          setRecommendations(fullText);
        },
        onCached: () => {
          setIsStreamingPreview(false);
          setStreamStatus('Using cached response...');
        },
        onComplete: () => {
          setIsStreamingPreview(false);
          setStreamStatus('');
        }
      });
      const responseTime = Date.now() - startTime;
      
      // Extract resolution steps from LLM
      const steps = response.resolution_steps || [];
      setRagResolutionSteps(steps);
      
      // Convert steps to formatted text for recommendations textarea
      const formattedRecommendations = formatResolutionStepsForDisplay(steps);
      setRecommendations(formattedRecommendations);
      
      // Keep resolution notes empty
      setResolutionNotes('');
      
      // Store related resources for the Related Resources section (remove duplicates)
      const resources = response.related_resources || [];
      const uniqueResources = [];
      const seenFilenames = new Set();
      
      resources.forEach(resource => {
        if (!seenFilenames.has(resource.filename)) {
          seenFilenames.add(resource.filename);
          uniqueResources.push(resource);
        }
      });
      
      setRelatedResources(uniqueResources);
      
      // Store similar tickets for reference
      setSimilarTickets(response.similar_items || []);
      
      // Cache the response data for this user + ticket
      setCachedResolutions(userEmail, ticketId, {
        steps,
        formattedRecommendations,
        uniqueResources,
        similar_items: response.similar_items || []
      });
      
      // Track automatic search interaction
      fetch('/api/track-search', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          searchQuery: query,
          ticketNumber: ticket.ticketId || ticketId,
          userName: userName || userEmail?.split('@')[0] || 'Anonymous',
          userEmail: userEmail || 'anonymous',
          searchType: 'automatic',
          results: {
            resolutionSteps: steps,
            similarTickets: response.similar_items || [],
            relatedResources: uniqueResources
          },
          responseTime: responseTime
        })
      }).catch(err => console.error('Error tracking search:', err));
      
    } catch (error) {
      console.error('Error fetching RAG resolutions:', error);
      setRagError('Failed to load AI recommendations. Please try again.');
    } finally {
      setRagLoading(false);
      setIsStreamingPreview(false);
      setStreamStatus('');
    }
  };

  const handleStepToggle = (index, stepText) => {
    const newSelected = new Set(selectedSteps);
    
    if (newSelected.has(index)) {
      // Remove step from resolution notes
      newSelected.delete(index);
      setResolutionNotes(prev => prev.replace(stepText + '\n', ''));
    } else {
      // Add step to resolution notes
      newSelected.add(index);
      setResolutionNotes(prev => prev + stepText + '\n');
    }
    
    setSelectedSteps(newSelected);
  };

  const [resolutionNotes, setResolutionNotes] = useState('');
  const [recommendations, setRecommendations] = useState('');
  const [state, setState] = useState('In Progress');
  const [resolutionCode, setResolutionCode] = useState('');
  const [resolutionSummary, setResolutionSummary] = useState('');
  const [onHoldReason, setOnHoldReason] = useState('');
  const [scheduleDate, setScheduleDate] = useState('');
  const [additionalComments, setAdditionalComments] = useState('');
  const [workNotes, setWorkNotes] = useState('');
  const [problemDetails, setProblemDetails] = useState('');
  const [vendorTicket, setVendorTicket] = useState('');
  const [configurationItem, setConfigurationItem] = useState('');

  const stateValueMap = {
    'New': '1',
    'In Progress': '2',
    'On Hold': '3',
    'Resolved': '6',
    'Closed': '7',
    'Canceled': '8',
    'Cancelled': '8'
  };

  const holdReasonValueMap = {
    'Awaiting caller': '1',
    'Awaiting evidence': '2',
    'Awaiting problem resolution': '3',
    'Awaiting vendor': '4',
    'Awaiting change': '5',
    'Scheduled with caller': '6'
  };

  const holdReasonCodeToLabelMap = {
    '1': 'Awaiting caller',
    '2': 'Awaiting evidence',
    '3': 'Awaiting problem resolution',
    '4': 'Awaiting vendor',
    '5': 'Awaiting change',
    '6': 'Scheduled with caller'
  };

  const formatDateToMmDdYyyy = (dateValue) => {
    if (!dateValue) return '';
    const [year, month, day] = dateValue.split('-');
    if (!year || !month || !day) return '';
    return `${month}/${day}/${year}`;
  };

  const problemIdPattern = /^PRB00\d{5}$/;
  const problemIdValue = problemDetails.trim().toUpperCase();
  const normalizedUserEmail = normalizeEmail(userEmail);
  const assignedUserEmail = normalizeEmail(ticket?.assignedToEmail);
  const isPrivilegedUser = EXCEPTION_EMAILS.has(normalizedUserEmail);
  const isUserInAssignmentGroup = groupUsers.some((user) => (user.email || '').trim().toLowerCase() === normalizedUserEmail);
  const canOperateTicket = isPrivilegedUser || isUserInAssignmentGroup;
  const originalPriorityValue = normalizePriorityValue(ticket?.priority);
  const isPriorityEditAllowed = Boolean(assignedUserEmail) && assignedUserEmail === normalizedUserEmail;
  const calculatedPriority = selectedImpact && selectedUrgency
    ? calculatePriorityFromImpactUrgency(selectedImpact, selectedUrgency)
    : '';
  const isPriorityChanged = Boolean(calculatedPriority) && calculatedPriority !== originalPriorityValue;

  const isOnHoldValid = (() => {
    if (state !== 'On Hold') return true;
    if (!onHoldReason) return false;

    if (onHoldReason === 'Awaiting caller') return additionalComments.trim() !== '';
    if (onHoldReason === 'Awaiting problem resolution') return problemIdPattern.test(problemIdValue);
    if (onHoldReason === 'Awaiting vendor') return vendorTicket.trim() !== '';
    if (onHoldReason === 'Scheduled with caller') return scheduleDate !== '';

    return true;
  })();

  const isResolvedValid = state !== 'Resolved' || (
    resolutionNotes.trim() !== '' &&
    resolutionCode.trim() !== '' &&
    resolutionSummary.trim() !== '' &&
    configurationItem.trim() !== ''
  );

  const isAssignmentSelectionValid = selectedAssignedTo.trim() !== '';
  const isUpdateAllowed = canOperateTicket && isAssignmentSelectionValid && isOnHoldValid && isResolvedValid;

  const formatFileSize = (sizeBytes) => {
    if (!Number.isFinite(sizeBytes) || sizeBytes < 0) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB'];
    let value = sizeBytes;
    let unitIndex = 0;
    while (value >= 1024 && unitIndex < units.length - 1) {
      value /= 1024;
      unitIndex += 1;
    }
    return `${value.toFixed(unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
  };

  const handleTicketUpdate = async () => {
    if (isSctask) return;
    if (!canOperateTicket) {
      alert('You can update this ticket only if your email belongs to this assignment group or you are in the approved exception list.');
      return;
    }

    const stateValue = stateValueMap[state];

    if (!stateValue) {
      alert('Please select a valid state.');
      return;
    }

    const payload = {
      state: stateValue,
      assigned_to: selectedAssignedTo.trim()
    };

    if (isPriorityChanged) {
      if (!isPriorityEditAllowed) {
        alert('Only the user currently assigned to this ticket can change Priority.');
        return;
      }
      payload.impact = selectedImpact;
      payload.urgency = selectedUrgency;
    }

    if (!selectedAssignedTo.trim()) {
      alert('Assigned To is mandatory. Please select a user.');
      return;
    }

    if (state === 'In Progress' && workNotes.trim()) payload.work_notes = workNotes.trim();
    if (state === 'In Progress' && additionalComments.trim()) payload.comments = additionalComments.trim();

    // Add work notes for ALL states (if provided)
    if (workNotes.trim() && state !== 'In Progress') {
      payload.work_notes = workNotes.trim();
    }

    if (state === 'On Hold') {
      const holdReasonValue = holdReasonValueMap[onHoldReason];
      if (!holdReasonValue) {
        alert('Please select On Hold Reason.');
        return;
      }

      payload.hold_reason = holdReasonValue;

      if (holdReasonValue === '1' && additionalComments.trim()) {
        payload.comments = additionalComments.trim();
      }

      if (holdReasonValue === '1' && !additionalComments.trim()) {
        alert('Additional Comments is mandatory for Awaiting Caller.');
        return;
      }

      if (holdReasonValue === '3' && !problemDetails.trim()) {
        alert('Problem is mandatory for Awaiting Problem Resolution.');
        return;
      }

      if (holdReasonValue === '3' && !problemIdPattern.test(problemIdValue)) {
        alert('Problem must be 10 characters and follow format PRB00XXXXX (example: PRB0012345).');
        return;
      }

      if (holdReasonValue === '4' && !vendorTicket.trim()) {
        alert('Vendor Ticket is mandatory for Awaiting Vendor.');
        return;
      }

      if (holdReasonValue === '6' && !scheduleDate) {
        alert('Scheduled Date is mandatory for Scheduled with Caller.');
        return;
      }

      if (holdReasonValue === '3') payload.problem_id = problemIdValue;
      if (holdReasonValue === '4') payload.u_vendor_ticket = vendorTicket.trim();
      if (holdReasonValue === '6') payload.u_scheduled_date = formatDateToMmDdYyyy(scheduleDate);
    }

    if (state === 'Resolved') {
      if (!resolutionCode.trim() || !resolutionSummary.trim() || !resolutionNotes.trim() || !configurationItem.trim()) {
        alert('Resolution Code, Resolution Summary, Resolution Notes, and Configuration Item are mandatory for Resolved state.');
        return;
      }

      payload.close_code = resolutionCode.trim();
      payload.u_resolution_summary = resolutionSummary.trim();
      payload.close_notes = resolutionNotes.trim();
      payload.cmdb_ci = configurationItem.trim();
    }

    if (!ticket.sysId) {
      alert('sys_id not available for this ticket. Please reload and try again.');
      return;
    }

    setUpdateLoading(true);
    try {
      const formData = new FormData();
      Object.entries({
        ...payload,
        userName: userName || '',
        userEmail: userEmail || ''
      }).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          formData.append(key, value);
        }
      });

      attachments.forEach((file) => {
        formData.append('attachments', file);
      });

      const response = await fetch(`/api/tickets/${ticket.sysId}`, {
        method: 'PATCH',
        body: formData
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        const message = result?.error || result?.message || 'Failed to update ticket';
        throw new Error(message);
      }

      const refreshedTicketResponse = await fetch(`/api/tickets/${ticketId}`, { method: 'GET' });
      const refreshedTicket = await refreshedTicketResponse.json();
      if (!refreshedTicketResponse.ok) {
        throw new Error(refreshedTicket?.error || 'Ticket updated but failed to refresh latest details');
      }
      applyTicketDetailsToState(refreshedTicket);

      const attachmentSummary = result?.attachments?.total > 0
        ? `\nAttachments uploaded: ${result.attachments.successful}/${result.attachments.total}`
        : '';
      const attachmentFailureSummary = result?.attachments?.failed > 0
        ? `\nSome attachments failed to upload.`
        : '';

      alert(`Ticket updated successfully in ServiceNow.${attachmentSummary}${attachmentFailureSummary}`);
      setAttachments([]);
    } catch (error) {
      console.error('Ticket update failed:', error);
      alert(error.message || 'Failed to update ticket. Please try again.');
    } finally {
      setUpdateLoading(false);
    }
  };

  const recommendationTexts = {
    investigate: 'Investigated the issue thoroughly and reviewed logs.',
    fixes: 'Applied necessary fixes including updates and configuration changes.',
    restart: 'Restarted SQL Server to apply changes and verify resolution.'
  };

  // Feedback handlers
  const handleThumbsUp = async () => {
    console.log('Thumbs up clicked for ticket:', ticketId);
    
    try {
      // Send positive feedback to backend
      const response = await fetch('/api/feedback', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ticketNumber: ticket.ticketId,
          feedbackType: 'thumbs_up',
          aiResponse: recommendations,
          userEmail: userEmail || 'anonymous',
          userName: userName || userEmail?.split('@')[0] || 'Anonymous',
          reason: '',
          comments: ''
        })
      });
      
      const result = await response.json();
      console.log('Feedback stored:', result);
      
      setFeedbackSubmitted(true);
      setTimeout(() => setFeedbackSubmitted(false), 2000);
    } catch (error) {
      console.error('Error submitting feedback:', error);
      alert('Failed to submit feedback. Please try again.');
    }
  };

  const handleThumbsDown = () => {
    setShowFeedbackModal(true);
  };

  const handleCopyResponse = async () => {
    try {
      await navigator.clipboard.writeText(recommendations);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
      
      // Track copy action as feedback
      fetch('/api/feedback', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ticketNumber: ticket.ticketId,
          feedbackType: 'copy',
          aiResponse: recommendations,
          userEmail: userEmail || 'anonymous',
          userName: userName || userEmail?.split('@')[0] || 'Anonymous',
          reason: '',
          comments: 'User copied the AI response'
        })
      }).catch(err => console.error('Error tracking copy:', err));
    } catch (err) {
      console.error('Failed to copy text:', err);
    }
  };

  const handleUserQuery = async () => {
    if (!userQuery.trim()) return;
    
    setUserQueryLoading(true);
    setRagLoading(true);
    setRagError(null);
    setIsFromCache(false);
    setIsStreamingPreview(false);
    setStreamStatus('Connecting to RAG stream...');
    const startTime = Date.now();
    const manualSearchRequestId = `manual-${ticketId || 'ticket'}-${Date.now()}`;
    
    try {
      // Send user query directly to RAG for focused results
      const response = await searchResolutionSteps(userQuery.trim(), {
        assignment_group: ticket.assignmentGroup || '',
        cmdb_ci: ticket.item || '',
        routing_app: ticket.routingServiceApp || '',
        // Force fresh retrieval for manual searches.
        disable_semantic_cache: true,
        bypass_cache: true,
        request_id: manualSearchRequestId,
      }, {
        onStatus: (message) => {
          setStreamStatus(message || 'Generating AI recommendations...');
        },
        onToken: (fullText) => {
          setIsStreamingPreview(true);
          setRecommendations(fullText);
        },
        onCached: () => {
          setIsStreamingPreview(false);
          setStreamStatus('Using cached response...');
        },
        onComplete: () => {
          setIsStreamingPreview(false);
          setStreamStatus('');
        }
      });
      const responseTime = Date.now() - startTime;
      
      // Extract resolution steps from response
      const steps = response.resolution_steps || [];
      
      // Update ragResolutionSteps only if new results exist; keep old ones otherwise
      if (steps.length > 0) {
        setRagResolutionSteps(steps);
        setSelectedSteps(new Set());
      }

      // Convert steps to formatted text
      const formattedRecommendations = formatResolutionStepsForDisplay(steps);
      
      // Replace recommendations with new RAG response
      setRecommendations(formattedRecommendations || 'No results found for your query. Please try different keywords.');
      
      // Update similar tickets if available
      if (response.similar_items) {
        setSimilarTickets(response.similar_items);
      }
      
      // Update related resources if available
      if (response.related_resources) {
        const resources = response.related_resources || [];
        const uniqueResources = [];
        const seenFilenames = new Set();
        
        resources.forEach(resource => {
          if (!seenFilenames.has(resource.filename)) {
            seenFilenames.add(resource.filename);
            uniqueResources.push(resource);
          }
        });
        
        setRelatedResources(uniqueResources);
      }
      
      // Track manual user query search
      fetch('/api/track-search', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          searchQuery: userQuery,
          ticketNumber: ticket.ticketId || ticketId,
          userName: userName || userEmail?.split('@')[0] || 'Anonymous',
          userEmail: userEmail || 'anonymous',
          searchType: 'manual',
          results: {
            resolutionSteps: steps,
            similarTickets: response.similar_items || [],
            relatedResources: response.related_resources || []
          },
          responseTime: responseTime
        })
      }).catch(err => console.error('Error tracking manual search:', err));
      
      setUserQuery('');
      setShowUserQueryInput(false);
    } catch (error) {
      console.error('User query error:', error);
      setRagError('Failed to generate recommendations. Please try again.');
    } finally {
      setUserQueryLoading(false);
      setRagLoading(false);
      setIsStreamingPreview(false);
      setStreamStatus('');
    }
  };

  const handleSubmitFeedback = async () => {
    console.log('Feedback submitted:', {
      ticketId,
      reason: feedbackReason,
      feedback: feedbackText,
      recommendations
    });
    
    try {
      // Send negative feedback to backend
      const response = await fetch('/api/feedback', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ticketNumber: ticket.ticketId,
          feedbackType: 'thumbs_down',
          reason: feedbackReason,
          comments: feedbackText,
          aiResponse: recommendations,
          userEmail: userEmail || 'anonymous',
          userName: userName || userEmail?.split('@')[0] || 'Anonymous'
        })
      });
      
      const result = await response.json();
      console.log('Feedback stored:', result);
      
      setShowFeedbackModal(false);
      setFeedbackText('');
      setFeedbackReason('');
      alert('Thank you for your feedback!');
    } catch (error) {
      console.error('Error submitting feedback:', error);
      alert('Failed to submit feedback. Please try again.');
    }
  };

  const [showNotesCard, setShowNotesCard] = useState(false);
  const [ticketNotes, setTicketNotes] = useState([]);
  const [fetchedComments, setFetchedComments] = useState([]);
  const [notesLoading, setNotesLoading] = useState(false);
  const [notesError, setNotesError] = useState('');
  const [notesSummary, setNotesSummary] = useState([]);
  const [commentsSummary, setCommentsSummary] = useState([]);

  const fetchTicketNotes = async () => {
    setNotesLoading(true);
    setNotesError('');
    setNotesSummary([]);
    setCommentsSummary([]);
    try {
      const response = await fetch(`/api/tickets/${ticketId}`);
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data?.error || 'Failed to fetch ticket notes');
      }
      const notes = Array.isArray(data.workNotesArray) ? data.workNotesArray : [];
      const comments = Array.isArray(data.commentsArray) ? data.commentsArray : [];
      
      setTicketNotes(notes);
      setFetchedComments(comments);
      
      // Summarize work notes separately
      if (notes.length > 0) {
        try {
          const notesSummaryResponse = await fetch('/api/summarize-notes', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ 
              workNotes: notes, 
              ticketId: ticketId,
              noteType: 'workNotes'
            })
          });
          const notesSummaryData = await notesSummaryResponse.json();
          console.log('Work Notes Summary response:', notesSummaryData);
          if (notesSummaryData.success && Array.isArray(notesSummaryData.summary) && notesSummaryData.summary.length > 0) {
            setNotesSummary(notesSummaryData.summary);
          }
        } catch (summaryErr) {
          console.error('Work notes AI summarization failed:', summaryErr);
        }
      }
      
      // Summarize additional comments separately
      if (comments.length > 0) {
        try {
          const commentsSummaryResponse = await fetch('/api/summarize-notes', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ 
              workNotes: comments, 
              ticketId: ticketId,
              noteType: 'comments'
            })
          });
          const commentsSummaryData = await commentsSummaryResponse.json();
          console.log('Comments Summary response:', commentsSummaryData);
          if (commentsSummaryData.success && Array.isArray(commentsSummaryData.summary) && commentsSummaryData.summary.length > 0) {
            setCommentsSummary(commentsSummaryData.summary);
          }
        } catch (summaryErr) {
          console.error('Comments AI summarization failed:', summaryErr);
        }
      }
    } catch (error) {
      console.error('Error fetching ticket notes:', error);
      setNotesError('Failed to load ticket notes. Please try again.');
    } finally {
      setNotesLoading(false);
    }
  };

  const toggleNotesCard = () => {
    if (!showNotesCard) {
      fetchTicketNotes();
    }
    setShowNotesCard(!showNotesCard);
  };

  if (ticketLoadError) return (
    <div className="not-found">
      <h2 style={{ color: '#ef4444', fontSize: 18, fontWeight: 600 }}>Unable to load ticket</h2>
      <p style={{ color: '#64748b', fontSize: 14, marginTop: 8 }}>{ticketLoadError}</p>
      <Link to="/AgentAssist" style={{ fontSize: 13 }}>Return to Dashboard</Link>
    </div>
  );

  if (!ticket || !ticket.ticketId) return (
    <div className="not-found">
      <FaSpinner className="spinner" style={{ fontSize: 36, color: '#7B3FF2' }} />
      <h2 style={{ color: '#475569', fontSize: 18, fontWeight: 600 }}>Loading ticket details...</h2>
      <Link to="/AgentAssist" style={{ fontSize: 13 }}>Return to Dashboard</Link>
    </div>
  );

  return (
    <div className="ticket-details-wrapper">
      <NavBar userName={userName} userEmail={userEmail} onLogout={onLogout} canViewUserAnalytics={canViewUserAnalytics} />
      
      <div className="ticket-details-content">
        {/* Breadcrumb / Back Link */}
        <Link to="/AgentAssist" className="back-link">
          <FaArrowLeft /> Back to Dashboard
        </Link>

        {/* Ticket Header Card */}
        <div className="ticket-header-card">
          <div className="ticket-header-top">
            <div>
              <h1 className="ticket-id">
                <FaTicketAlt style={{ fontSize: 18, marginRight: 6, verticalAlign: 'middle' }} />
                Ticket #{ticket.ticketId}
              </h1>
              <p className="ticket-description"><strong>Short Description:</strong> {ticket.shortDescription}</p>
              {ticket.description && (
                <p className="ticket-description" style={{ marginTop: 4, color: '#94a3b8', fontSize: 13 }}><strong>Description:</strong> {ticket.description}</p>
              )}
            </div>
            <span className={`status-badge status-${ticket.status?.toLowerCase().replace(' ', '-')}`}>
              {ticket.status}
            </span>
          </div>
          
          <div className="ticket-info-grid">
            <div className="info-item">
              <span className="info-label">Received</span>
              <span className="info-value">{ticket.receivedOn}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Created By</span>
              <span className="info-value">{ticket.createdBy || 'N/A'}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Assignment Group</span>
              <span className="info-value">{ticket.assignmentGroup}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Assigned To</span>
              <span className="info-value">{ticket.assignedTo}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Item</span>
              <span className="info-value">{ticket.item}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Priority</span>
              <span className={`info-value priority-${ticket.priority?.toLowerCase()}`}>
                {ticket.priority}
              </span>
            </div>
          </div>
        </div>

        {/* Main Content - Stacked Layout */}
        {/* Recommendations Card - Full Width */}
        <div className="card recommendations-card full-width-card">
            <h3 className="card-title"><FaRegLightbulb style={{ color: '#7B3FF2' }} /> Recommendations</h3>
            {ragLoading && !isFromCache && (
              <div className="rag-status">
                <FaSpinner className="spinner" /> {streamStatus || 'Generating AI recommendations...'}
              </div>
            )}
            {!ragLoading && !isStreamingPreview && recommendations && ragResolutionSteps.length > 0 && !isFromCache && (
              <div className="rag-status success">
                <FaCheck style={{ fontSize: 12 }} /> AI-generated from {similarTickets.length} similar tickets
              </div>
            )}
            <div className="recommendations-content">
              <div className="rec-rendered-wrap">
                {recommendations
                  ? <RecommendationsRenderer text={recommendations} />
                  : <p className="rec-placeholder">AI-generated recommendations will appear here...</p>
                }
              </div>
              {!canOperateTicket && (
                <p style={{ color: '#ef4444', fontSize: 12, marginTop: 8 }}>
                  You cannot use Resolution Recommendations for this ticket because your email is not mapped to this assignment group.
                </p>
              )}
              {/* Feedback Buttons */}
              {!ragLoading && recommendations && (
                <div className="feedback-buttons">
                  <button 
                    className="feedback-btn thumbs-up" 
                    onClick={handleThumbsUp}
                    disabled={!canOperateTicket}
                    title="Helpful response"
                  >
                    <FaThumbsUp /> {feedbackSubmitted ? 'Thanks!' : 'Helpful'}
                  </button>
                  <button 
                    className="feedback-btn thumbs-down" 
                    onClick={handleThumbsDown}
                    disabled={!canOperateTicket}
                    title="Not helpful"
                  >
                    <FaThumbsDown /> Not helpful
                  </button>
                  <button 
                    className="feedback-btn copy-btn" 
                    onClick={handleCopyResponse}
                    disabled={!canOperateTicket}
                    title="Copy to clipboard"
                  >
                    <FaCopy /> {copySuccess ? 'Copied!' : 'Copy'}
                  </button>
                  <button 
                    className="feedback-btn userquery-btn" 
                    onClick={() => setShowUserQueryInput(!showUserQueryInput)}
                    title="Ask a question"
                  >
                    <FaSearch /> Search Manually
                  </button>
                  <button 
                    className="feedback-btn notes-history-btn" 
                    onClick={toggleNotesCard}
                    title="View AI generated notes summary"
                  >
                    <FaFileAlt /> {showNotesCard ? 'Hide Work Notes Summary' : 'Work Notes Summary'}
                  </button>
                </div>
              )}

              {/* Notes Summary Section */}
              {showNotesCard && (
                <div className="notes-card">
                  {notesLoading ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, color: '#7B3FF2', fontSize: 12 }}>
                      <FaSpinner className="spinner" /> Summarizing...
                    </div>
                  ) : notesError ? (
                    <p className="error-message">{notesError}</p>
                  ) : ticketNotes.length === 0 && fetchedComments.length === 0 ? (
                    <div style={{ color: '#888', marginTop: 6, fontSize: 12 }}>No work notes or comments available.</div>
                  ) : (
                    <div>
                      {/* Work Notes Summary */}
                      {ticketNotes.length > 0 && (
                        <div style={{ marginBottom: 16 }}>
                          <strong style={{ fontSize: 13, color: '#1e293b', display: 'block', marginBottom: 8 }}>
                            <FaFileAlt style={{ marginRight: 6, color: '#7B3FF2' }} />Work Notes Summary
                          </strong>
                          {notesSummary.length > 0 ? (
                            <ul style={{ margin: 0, padding: 0, paddingLeft: 16, listStyle: 'disc', display: 'flex', flexDirection: 'column', gap: 6 }}>
                              {notesSummary.map((item, idx) => {
                                const date = typeof item === 'string' ? '' : item.date;
                                const summary = typeof item === 'string' ? item : item.summary;
                                return (
                                  <li key={idx} style={{ fontSize: 12, color: '#334155', lineHeight: 1.5 }}>
                                    {date && <span style={{ fontSize: 11, fontWeight: 600, color: '#7B3FF2' }}>[{date}] </span>}
                                    {summary}
                                  </li>
                                );
                              })}
                            </ul>
                          ) : (
                            <div style={{ color: '#94a3b8', fontSize: 12, fontStyle: 'italic' }}>
                              AI summary not available for work notes.
                            </div>
                          )}
                        </div>
                      )}
                      
                      {/* Additional Comments Summary */}
                      {fetchedComments.length > 0 && (
                        <div>
                          <strong style={{ fontSize: 13, color: '#1e293b', display: 'block', marginBottom: 8 }}>
                            <FaComment style={{ marginRight: 6, color: '#10b981' }} />Additional Comments Summary
                          </strong>
                          {commentsSummary.length > 0 ? (
                            <ul style={{ margin: 0, padding: 0, paddingLeft: 16, listStyle: 'disc', display: 'flex', flexDirection: 'column', gap: 6 }}>
                              {commentsSummary.map((item, idx) => {
                                const date = typeof item === 'string' ? '' : item.date;
                                const summary = typeof item === 'string' ? item : item.summary;
                                return (
                                  <li key={idx} style={{ fontSize: 12, color: '#334155', lineHeight: 1.5 }}>
                                    {date && <span style={{ fontSize: 11, fontWeight: 600, color: '#10b981' }}>[{date}] </span>}
                                    {summary}
                                  </li>
                                );
                              })}
                            </ul>
                          ) : (
                            <div style={{ color: '#94a3b8', fontSize: 12, fontStyle: 'italic' }}>
                              AI summary not available for comments.
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
              
              {/* User Query Input Section */}
              {showUserQueryInput && (
                <div className="userquery-section">
                  <textarea 
                    placeholder="Please enter the issue in detail..."
                    rows="1"
                    value={userQuery}
                    onChange={(e) => setUserQuery(e.target.value)}
                  />
                  <div className="userquery-actions">
                    <button 
                      className="userquery-send-btn"
                      onClick={handleUserQuery}
                      disabled={userQueryLoading || !userQuery.trim()}
                    >
                      {userQueryLoading ? <><FaSpinner className="spinner"/> Thinking...</> : <><FaPaperPlane /> Send</>}
                    </button>
                    <button 
                      className="userquery-cancel-btn"
                      onClick={() => {
                        setShowUserQueryInput(false);
                        setUserQuery('');
                      }}
                    >
                      <FaTimes /> Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
        </div>

        {/* Update ticket in ServiceNow - Full Width */}
        <div className="card resolution-card full-width-card">
            <h3 className="card-title"><FaSyncAlt style={{ color: '#7B3FF2' }} /> Update ticket in ServiceNow</h3>
            {isSctask ? (
              <p
                className="sctask-update-disabled"
                role="status"
                style={{ margin: 0, padding: '14px 16px', border: '1px solid #e2e8f0', borderRadius: 8, background: '#f8fafc', color: '#64748b', fontSize: 13 }}
              >
                Updates are currently unavailable for SCTASK tickets.
              </p>
            ) : <>
            <div className="dropdown-group">
              <>
                <label>Assigned To *</label>
                {!groupUsersLoading && !canOperateTicket && (
                  <p style={{ color: '#ef4444', fontSize: 12, marginBottom: 8 }}>
                    You do not have access for this ticket's assignment group.
                  </p>
                )}
                {groupUsersLoading ? (
                  <p style={{ color: '#64748b', fontSize: 13 }}>Loading users for assignment group...</p>
                ) : (
                  <select value={selectedAssignedTo} onChange={(e) => setSelectedAssignedTo(e.target.value)} disabled={!canOperateTicket}>
                    <option value="">Select user</option>
                    {groupUsers.map((user) => (
                      <option key={user.email} value={user.name || user.email}>
                        {user.name ? `${user.name} (${user.email})` : user.email}
                      </option>
                    ))}
                  </select>
                )}
              </>

              <>
                <label>State</label>
                <select value={state} onChange={(e) => setState(e.target.value)} disabled={!canOperateTicket}>
                  <option>In Progress</option>
                  <option>On Hold</option>
                  <option>Resolved</option>
                  <option>Canceled</option>
                </select>
              </>

              <>
                <label>Impact / Urgency / Priority</label>
                {!isPriorityEditAllowed && (
                  <p style={{ color: '#ef4444', fontSize: 12, marginBottom: 4 }}>
                    Only the currently assigned user can change Impact / Urgency.
                  </p>
                )}
                <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: '#64748b', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.4px' }}>Impact</div>
                    <select
                      value={selectedImpact}
                      onChange={(e) => setSelectedImpact(e.target.value)}
                      disabled={!canOperateTicket || !isPriorityEditAllowed}
                      style={{ width: '100%' }}
                    >
                      <option value="">Select</option>
                      <option value="1">1 - High</option>
                      <option value="2">2 - Medium</option>
                      <option value="3">3 - Low</option>
                    </select>
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: '#64748b', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.4px' }}>Urgency</div>
                    <select
                      value={selectedUrgency}
                      onChange={(e) => setSelectedUrgency(e.target.value)}
                      disabled={!canOperateTicket || !isPriorityEditAllowed}
                      style={{ width: '100%' }}
                    >
                      <option value="">Select</option>
                      <option value="1">1 - High</option>
                      <option value="2">2 - Medium</option>
                      <option value="3">3 - Low</option>
                    </select>
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: '#64748b', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.4px' }}>Priority</div>
                    <div style={{
                      padding: '10px 14px',
                      border: '1.5px solid #e4e8f0',
                      borderRadius: '10px',
                      background: '#f8faff',
                      fontSize: 14,
                      fontWeight: 700,
                      color: '#334155',
                      minHeight: '42px',
                      display: 'flex',
                      alignItems: 'center'
                    }}>
                      {calculatedPriority || normalizePriorityValue(ticket?.priority) || '—'}
                    </div>
                  </div>
                </div>
              </>

              <>
                <label>Work Notes</label>
                <textarea
                  placeholder="Enter work notes here..."
                  rows="3"
                  value={workNotes}
                  onChange={(e) => setWorkNotes(e.target.value)}
                  disabled={!canOperateTicket}
                />
              </>

              {state === 'In Progress' && (
                <>
                  <label>Additional Comments</label>
                  <textarea
                    placeholder="Enter additional comments here..."
                    rows="3"
                    value={additionalComments}
                    onChange={(e) => setAdditionalComments(e.target.value)}
                    disabled={!canOperateTicket}
                  />
                </>
              )}

              <>
                <label>Attachments</label>
                <input
                  type="file"
                  multiple
                  onChange={(e) => setAttachments(Array.from(e.target.files || []))}
                  disabled={!canOperateTicket || updateLoading}
                />
                {attachments.length > 0 && (
                  <div style={{ fontSize: 12, color: '#475569' }}>
                    {attachments.map((file, index) => (
                      <div key={`${file.name}-${index}`}>
                        {file.name} ({formatFileSize(file.size)})
                      </div>
                    ))}
                  </div>
                )}
              </>

              {state === 'On Hold' && (
              <>
                <label>On Hold Reason</label>
                <select value={onHoldReason} onChange={(e) => setOnHoldReason(e.target.value)} disabled={!canOperateTicket}>
                  <option value="">--none</option>
                  <option>Awaiting caller</option>
                  <option>Awaiting evidence</option>
                  <option>Awaiting problem resolution</option>
                  <option>Awaiting vendor</option>
                  <option>Awaiting change</option>
                  <option>Scheduled with caller</option>
                </select>

                {onHoldReason === 'Scheduled with caller' && (
                  <>
                    <label>Schedule Date *</label>
                    <input type="date" value={scheduleDate} onChange={(e) => setScheduleDate(e.target.value)} required disabled={!canOperateTicket} />
                  </>
                )}

                {onHoldReason === 'Awaiting caller' && (
                  <>
                    <label>Additional comments (visible to customer) *</label>
                    <textarea value={additionalComments} onChange={(e) => setAdditionalComments(e.target.value)} required disabled={!canOperateTicket} />
                  </>
                )}

                {onHoldReason === 'Awaiting problem resolution' && (
                  <>
                    <label>Problems *</label>
                    <input type="text" value={problemDetails} onChange={(e) => setProblemDetails(e.target.value)} required disabled={!canOperateTicket} />
                    {problemDetails.trim() !== '' && !problemIdPattern.test(problemIdValue) && (
                      <p style={{ color: '#ef4444', fontSize: 12, marginTop: 4 }}>
                        Enter valid Problem ID format: PRB00XXXXX (e.g., PRB0012345)
                      </p>
                    )}
                  </>
                )}

                {onHoldReason === 'Awaiting vendor' && (
                  <>
                    <label>Vendor Ticket *</label>
                    <input type="text" value={vendorTicket} onChange={(e) => setVendorTicket(e.target.value)} required disabled={!canOperateTicket} />
                  </>
                )}
              </>
            )}

            {state === 'Resolved' && (
              <>
                <label>Resolution Code *</label>
                <select value={resolutionCode} onChange={(e) => setResolutionCode(e.target.value)} disabled={!canOperateTicket}>
                  <option value="">SELECTED</option>
                  <option>Solved (Work Around)</option>
                  <option>Solved (Permanently)</option>
                  <option>Solved Remotely (Work Around)</option>
                  <option>Solved Remotely (Permanently)</option>
                  <option>Not Solved (Not Reproducible)</option>
                  <option>Not Solved (Too Costly)</option>
                  <option>Closed/Resolved by Caller</option>
                </select>

                <label>Resolution Summary *</label>
                <select value={resolutionSummary} onChange={(e) => setResolutionSummary(e.target.value)} disabled={!canOperateTicket}>
                  <option value="">-- None --</option>
                  <option>Capacity Increased</option>
                  <option>No Action Taken</option>
                  <option>Patch/Update Applied</option>
                  <option>Performance Improvement Implemented</option>
                  <option>Password Reset</option>
                  <option>Configuration Updated</option>
                  <option>User Advised/Trained on Process</option>
                  <option>Data/File/Software Issue Fixed</option>
                  <option>Out of Scope/Best Effort Provided</option>
                  <option>PC Trust Relationship Repaired</option>
                  <option>Hardware Repaired/Refreshed/Replaced</option>
                  <option>Malware/Virus Removed</option>
                  <option>Failing Component Restarted/Rebooted</option>
                  <option>No Response from Caller</option>
                  <option>Synchronization Issue</option>
                  <option>Permission Issue/Access Request Required</option>
                  <option>Compliance Training</option>
                  <option>Services Restored by Vendor</option>
                  <option>Resolved from Jira</option>
                </select>
                
                <label>Resolution Notes *</label>
                <textarea
                  placeholder="Enter resolution notes here..."
                  rows="6"
                  value={resolutionNotes}
                  onChange={(e) => setResolutionNotes(e.target.value)}
                  disabled={!canOperateTicket}
                  required
                />

                <label>Configuration Item *</label>
                <input
                  type="text"
                  placeholder="Enter configuration item"
                  value={configurationItem}
                  onChange={(e) => setConfigurationItem(e.target.value)}
                  disabled={!canOperateTicket}
                  required
                />
              </>
            )}
          </div>

            <div className="button-group">
              {state !== 'Resolved' && (
                <button className="btn-primary" onClick={handleTicketUpdate} disabled={updateLoading || !isUpdateAllowed}>
                  {updateLoading ? <><FaSpinner className="spinner" /> Updating...</> : <><FaSave /> Update</>}
                </button>
              )}
              {state === 'Resolved' && (
                <button className="btn-success" onClick={handleTicketUpdate} disabled={updateLoading || !isUpdateAllowed}>
                  {updateLoading ? <><FaSpinner className="spinner" /> Resolving...</> : <><FaCheck /> Resolve</>}
                </button>
              )}
            </div>
            </>}
          </div>

        {/* Resources Panel - Full Width with Side by Side */}
        <div className="card resources-card full-width-card">
          <h3 className="card-title">
            <FaFileAlt /> Related Resources
          </h3>
          <div className="resources-side-by-side">
            {/* Similar Tickets Column */}
            <div className="resources-column">
              <h4 className="resources-column-title">
                <FaTicketAlt style={{ color: '#7B3FF2', fontSize: 13 }} /> Similar Tickets ({similarTickets.length})
              </h4>
              <div className="documents-list">
                {ragLoading && <p>Loading resources...</p>}
                {!ragLoading && (
                  <>
                    {similarTickets.length > 0 ? (
                      <ul className="resource-list">
                        {similarTickets.map((ticket, index) => {
                          let ticketNumber = ticket.number || ticket.id || '';
                          ticketNumber = ticketNumber.replace('.json', '');
                          if (!ticketNumber && ticket.download_url) {
                            const match = ticket.download_url.match(/INC\d+/);
                            if (match) ticketNumber = match[0];
                          }
                          const serviceNowUrl = ticketNumber 
                            ? `https://tranetechnologies.service-now.com/nav_to.do?uri=incident.do?sysparm_query=number=${ticketNumber}`
                            : ticket.download_url;
                          return (
                            <li key={index}>
                              <span><FaTicketAlt style={{ color: '#7B3FF2', marginRight: 6, fontSize: 12 }} />{ticketNumber}</span>
                              {serviceNowUrl && (
                                <a href={serviceNowUrl} target="_blank" rel="noopener noreferrer" className="btn-download">
                                  <FaExternalLinkAlt style={{ fontSize: 10, marginRight: 4 }} /> View
                                </a>
                              )}
                            </li>
                          );
                        })}
                      </ul>
                    ) : (
                      <p style={{color: '#94a3b8', fontStyle: 'italic', fontSize: 13, textAlign: 'center', padding: '20px 0'}}>
                        No similar tickets found for this issue.
                      </p>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Documents Column */}
            <div className="resources-column">
              <h4 className="resources-column-title">
                <FaFileAlt style={{ color: '#6366f1', fontSize: 13 }} /> Documents ({relatedResources.length})
              </h4>
              <div className="documents-list">
                {ragLoading && <p>Loading resources...</p>}
                {!ragLoading && (
                  <>
                    {relatedResources.length > 0 ? (
                      <ul className="resource-list">
                        {relatedResources.map((resource, index) => (
                          <li key={index}>
                            <span><FaFileAlt style={{ color: '#6366f1', marginRight: 6, fontSize: 12 }} />{resource.filename}</span>
                            {resource.download_url && (
                              <a href={resource.download_url} target="_blank" rel="noopener noreferrer" className="btn-download">
                                <FaExternalLinkAlt style={{ fontSize: 10, marginRight: 4 }} /> View
                              </a>
                            )}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p style={{color: '#94a3b8', fontStyle: 'italic', fontSize: 13, textAlign: 'center', padding: '20px 0'}}>
                        No related documents found for this ticket.
                      </p>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
        
        {/* Bottom Bar */}
        <div className="login-bottom-bar">
          <div className="bottom-bar-content">
            <img src={wiproLogo} alt="Wipro" className="bottom-bar-wipro-logo" />
            <p className="bottom-bar-copyright">Copyright © 2026 Wipro LTD. All rights reserved.</p>
          </div>
        </div>
      </div>

      {/* Feedback Modal */}
      {showFeedbackModal && (
        <div className="modal-overlay" onClick={() => setShowFeedbackModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
              <div>
                <h3 style={{ margin: 0 }}>Help us improve</h3>
                <p style={{ margin: '4px 0 0', fontSize: 13, color: '#94a3b8' }}>What was wrong with the AI response?</p>
              </div>
              <button 
                onClick={() => setShowFeedbackModal(false)}
                style={{ background: '#f1f5f9', border: 'none', width: 30, height: 30, borderRadius: 8, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', fontSize: 12 }}
              >
                <FaTimes />
              </button>
            </div>
            
            <label style={{ display: 'block', marginBottom: 6, fontWeight: 700, fontSize: 12, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Reason
            </label>
            <select
              value={feedbackReason}
              onChange={(e) => setFeedbackReason(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 14px',
                border: '1.5px solid #e4e8f0',
                borderRadius: '10px',
                fontSize: '14px',
                marginBottom: '16px',
                fontFamily: 'inherit',
                color: '#334155',
                backgroundColor: '#fafbfd',
                boxSizing: 'border-box',
                appearance: 'none',
                backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='7' viewBox='0 0 12 7'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%2394a3b8' stroke-width='1.5' fill='none' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E\")",
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 14px center',
                paddingRight: '36px',
                cursor: 'pointer'
              }}
            >
              <option value="">-- Select a reason --</option>
              <option value="No response received">No response received</option>
              <option value="Response is not relevant">Response is not relevant</option>
              <option value="System performance issue">System performance issue</option>
              <option value="Incomplete information">Incomplete information</option>
              <option value="Incorrect solution">Incorrect solution</option>
              <option value="Other">Other</option>
            </select>
            
            <label style={{ display: 'block', marginBottom: 6, fontWeight: 700, fontSize: 12, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Additional comments
            </label>
            <textarea
              value={feedbackText}
              onChange={(e) => setFeedbackText(e.target.value)}
              placeholder="What could be improved? What was missing?"
              rows="5"
              style={{
                width: '100%',
                padding: '12px 14px',
                border: '1.5px solid #e4e8f0',
                borderRadius: '10px',
                fontSize: '14px',
                marginBottom: '18px',
                fontFamily: 'inherit',
                boxSizing: 'border-box',
                background: '#fafbfd',
                resize: 'vertical',
                lineHeight: 1.6
              }}
            />
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button 
                onClick={() => setShowFeedbackModal(false)}
                style={{
                  padding: '10px 20px',
                  background: 'white',
                  border: '1.5px solid #e4e8f0',
                  borderRadius: '10px',
                  cursor: 'pointer',
                  fontWeight: 600,
                  fontSize: 13,
                  color: '#64748b',
                  transition: 'all 0.15s ease'
                }}
              >
                Cancel
              </button>
              <button 
                onClick={handleSubmitFeedback}
                disabled={!feedbackText.trim()}
                style={{
                  padding: '10px 20px',
                  background: feedbackText.trim() ? 'linear-gradient(135deg, #7B3FF2 0%, #6025c9 100%)' : '#e4e8f0',
                  color: feedbackText.trim() ? 'white' : '#94a3b8',
                  border: 'none',
                  borderRadius: '10px',
                  cursor: feedbackText.trim() ? 'pointer' : 'not-allowed',
                  fontWeight: 600,
                  fontSize: 13,
                  boxShadow: feedbackText.trim() ? '0 2px 8px rgba(123, 63, 242, 0.22)' : 'none',
                  transition: 'all 0.2s ease'
                }}
              >
                Submit Feedback
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default TicketDetailsPage;
